import { useState, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import useBowler from "../../../hooks/useBowler";
import { eventsBySquadFromRows } from "../../../utility/signupAvailability";

export const useTeamCreationData = (
  tournamentID,
  currentOrganization,
  squadId = null
) => {
  // Data state
  const [events, setEvents] = useState([]);
  const [sidePotEvents, setSidePotEvents] = useState([]);
  const [allEventConfigs, setAllEventConfigs] = useState([]); // All event configurations
  const [tournamentBowlers, setTournamentBowlers] = useState([]);
  const [eventsBySquad, setEventsBySquad] = useState({});
  const [extrasReady, setExtrasReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use the existing useBowler hook for bowler data
  const { existingBowlers, error: bowlerError } = useBowler(
    currentOrganization?.id
  );

  // Load events and tournament bowlers on mount or when squad changes
  useEffect(() => {
    const loadData = async () => {
      if (!tournamentID || !currentOrganization?.id) return;

      setLoading(true);
      setError(null);

      try {
        await Promise.all([
          loadEvents(),
          loadTournamentBowlers(),
          loadAllEventConfigs(),
          loadExtras(),
        ]);
      } catch (err) {
        console.error("Error loading team creation data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tournamentID, currentOrganization?.id, squadId]);

  const loadExtras = async () => {
    setExtrasReady(false);
    try {
      const { data: squadRows, error: squadError } = await supabase
        .from("squads")
        .select("id")
        .eq("tournamentID", tournamentID)
        .eq("isDeleted", false);
      if (squadError) throw squadError;
      const ids = (squadRows || []).map((row) => row.id);
      if (!ids.length) {
        setEventsBySquad({});
        return;
      }
      const { data: extraRows, error: extraError } = await supabase
        .from("squadEvents")
        .select("squadID, eventsID")
        .in("squadID", ids);
      setEventsBySquad(
        extraError ? {} : eventsBySquadFromRows(extraRows || [])
      );
    } catch (error) {
      console.error("Error loading squad events:", error);
      setEventsBySquad({});
    } finally {
      setExtrasReady(true);
    }
  };

  const loadEvents = async () => {
    try {
      // Load primary events (always tournament-wide, templateId IS NULL)
      const { data: primaryEvents, error: primaryError } = await supabase
        .from("tournamentEvents")
        .select("*")
        .eq("tournamentID", tournamentID)
        .eq("isPrimary", true)
        .eq("isDeleted", false)
        .is("templateId", null) // Only tournament-wide primary events
        .order("created");

      if (primaryError) throw primaryError;

      // Load side pot events with their fees
      // Include: tournament-wide side pots (templateId IS NULL) + squad-specific side pots for selected squad
      let sidePotQuery = supabase
        .from("tournamentEvents")
        .select(
          `
          *,
          eventFee (
            id,
            fees (
              feeName
            ),
            amount
          )
        `
        )
        .eq("tournamentID", tournamentID)
        .eq("isPrimary", false)
        .eq("isDeleted", false);

      // Filter side pot events:
      // - Tournament-wide: templateId IS NULL
      // - Squad-specific: templateId IS NOT NULL AND squadId = selected squad
      if (squadId) {
        // Include both tournament-wide and squad-specific events for this squad
        sidePotQuery = sidePotQuery.or(
          `templateId.is.null,squadId.eq.${squadId}`
        );
      } else {
        // No squad selected yet, only show tournament-wide events
        sidePotQuery = sidePotQuery.is("templateId", null);
      }

      const { data: sidePotData, error: sidePotError } = await sidePotQuery;

      if (sidePotError) throw sidePotError;

      // Process side pot events to include entry fees
      const processedSidePotEvents =
        sidePotData?.map((event) => {
          const fees = event.eventFee || [];
          const totalEntryFee = fees.reduce(
            (sum, fee) => sum + (fee.amount || 0),
            0
          );
          return {
            ...event,
            entryFee: totalEntryFee,
          };
        }) || [];

      setEvents(primaryEvents || []);
      setSidePotEvents(processedSidePotEvents);
    } catch (error) {
      console.error("Error loading events:", error);
      throw error;
    }
  };

  const loadAllEventConfigs = async () => {
    try {
      // Get all all event configurations for this tournament in a single query
      const { data: configs, error: configError } = await supabase.rpc(
        "get_all_event_configurations_for_tournament",
        { input_tournament_id: tournamentID }
      );

      if (configError) {
        console.error("Error loading all event configs:", configError);
        // Don't throw - all events are optional
        setAllEventConfigs([]);
        return;
      }

      // Transform to match expected format
      const transformedConfigs = (configs || []).map((config) => ({
        allEventId: config.all_event_id,
        allEventName: config.all_event_name,
        primaryEventIds: config.primary_event_ids || [],
        secondaryRuleType: config.secondary_rule_type || "first_attempt",
      }));

      setAllEventConfigs(transformedConfigs);
    } catch (error) {
      console.error("Error loading all event configs:", error);
      // Don't throw - all events are optional
      setAllEventConfigs([]);
    }
  };

  const loadTournamentBowlers = async () => {
    if (!tournamentID || !currentOrganization?.id) return;

    try {
      const { data, error } = await supabase
        .from("tournamentAverages")
        .select(
          `
          average,
          "hasAverage",
          bowlers (
            id,
            name,
            "usbcID",
            bowlersContact (
              type,
              value
            )
          )
        `
        )
        .eq("tournamentID", tournamentID)
        .eq("bowlers.organizationID", currentOrganization.id);

      if (error) throw error;

      // Process the data to match the expected format
      const processedBowlers =
        data?.map((item) => {
          const bowler = item.bowlers;
          if (!bowler) return null; // Skip if bowler is null
          const contacts = bowler.bowlersContact || [];

          // Extract email and phone from contacts
          const email = contacts.find((c) => c.type === "email")?.value || "";
          const phone = contacts.find((c) => c.type === "phone")?.value || "";

          return {
            id: bowler.id,
            name: bowler.name,
            usbcID: bowler.usbcID || "",
            average: item.average,
            hasAverage: item.hasAverage === false ? false : true, // Use stored hasAverage value, default to true if null/undefined
            email: email,
            phone: phone,
            isNew: false, // These are existing bowlers
          };
        }) || [];

      setTournamentBowlers(processedBowlers.filter(Boolean));
    } catch (error) {
      console.error("Could not load tournament bowler data:", error);
      setTournamentBowlers([]);
    }
  };

  return {
    events,
    sidePotEvents,
    allEventConfigs,
    tournamentBowlers,
    eventsBySquad,
    extrasReady,
    existingBowlers,
    loading,
    error: error || bowlerError,
  };
};
